import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';

interface DatePickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export function DatePicker({ selectedDate, onDateChange }: DatePickerProps) {
  const today = new Date();

  const dateOptions = useMemo(
    () =>
      Array.from({ length: 11 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date;
      }).sort((a, b) => b.getTime() - a.getTime()),
    [selectedDate],
  );

  const minDate = dateOptions[dateOptions.length - 1];

  const formatDateOption = (date: Date): string => {
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const goToPreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);

    if (prevDay >= minDate) {
      onDateChange(prevDay);
    }
  };

  const goToNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);

    if (nextDay <= today) {
      onDateChange(nextDay);
    }
  };

  const isPreviousDisabled = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    return prevDay < minDate;
  };

  const isNextDisabled = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return nextDay > today;
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center space-x-2">
        <button
          onClick={goToPreviousDay}
          disabled={isPreviousDisabled()}
          className={`flex h-8 w-8 items-center justify-center rounded-full ${
            isPreviousDisabled()
              ? 'cursor-not-allowed text-gray-400'
              : 'text-white transition-colors hover:bg-white/20'
          }`}
          aria-label="Previous day"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="group relative inline-flex items-center">
          <select
            id="date-picker"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={e => {
              const date = new Date(e.target.value);
              onDateChange(date);
            }}
            className="cursor-pointer appearance-none rounded-md border border-purple-300/30 bg-white/15 px-4 py-1.5 text-white shadow-inner backdrop-blur-sm transition-all hover:bg-white/20 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
            aria-label="Select date"
          >
            {dateOptions.map(date => (
              <option
                key={date.toISOString()}
                value={date.toISOString().split('T')[0]}
                className="bg-gray-800 text-white"
              >
                {formatDateOption(date)}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={goToNextDay}
          disabled={isNextDisabled()}
          className={`flex h-8 w-8 items-center justify-center rounded-full ${
            isNextDisabled()
              ? 'cursor-not-allowed text-gray-400'
              : 'text-white transition-colors hover:bg-white/20'
          }`}
          aria-label="Next day"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
